import React, { useState, useEffect, useCallback, Component, ErrorInfo, ReactNode } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Box,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme as useMuiTheme,
  Button,
  Fade,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Sun,
  Footprints,
  Scale,
  Utensils,
  Heart,
  Pill,
  Ruler,
  TrendingUp,
  BarChart3,
  PieChart,
  Camera,
  Settings as SettingsIcon,
  PlusCircle,
  User,
  RefreshCw,
  AlertTriangle,
  Stethoscope,
  Beaker,
  Activity,
  FileText,
  Dumbbell,
} from 'lucide-react';
import { format } from 'date-fns';
import { theme } from './theme';
import {
  SectionValue,
  LogSubTab,
  BodySubTab,
  HealthSubTab,
  InsightsSubTab,
  WalkEntry,
} from './types';
import { useOptimizedData } from './hooks/useOptimizedData';
import { isAuthenticated, clearAuth } from './utils/auth';
import { ToastProvider } from './components/shared/ToastProvider';
import { SkeletonLoader } from './components/shared/SkeletonLoader';
import { BottomNav } from './components/BottomNav';
import { SubTabNav } from './components/SubTabNav';

import { Login } from './components/Login';
import { Today } from './components/today/Today';
import { WeightTracker } from './components/WeightTracker';
import { MealPlanner } from './components/MealPlanner';
import { ExerciseTracker } from './components/ExerciseTracker';
import { SupplementTracker } from './components/SupplementTracker';
import { BodyMeasurementTracker } from './components/BodyMeasurementTracker';
import { ProgressTracker } from './components/ProgressTracker';
import { PerformanceDashboard } from './components/PerformanceDashboard';
import { Statistics } from './components/Statistics';
import { PhotoProgressTracker } from './components/PhotoProgressTracker';
import { Settings as SettingsComponent } from './components/Settings';
import { WalkLogger } from './components/log/WalkLogger';
import { DailyFoodLog } from './components/log/DailyFoodLog';
import { BloodPressureTracker } from './components/health/BloodPressureTracker';
import { LabResults } from './components/health/LabResults';
import { RiskScores } from './components/health/RiskScores';
import { MedicalHistory } from './components/health/MedicalHistory';

interface ErrorBoundaryProps { children: ReactNode; }
interface ErrorBoundaryState { hasError: boolean; error: Error | null; }

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error): ErrorBoundaryState { return { hasError: true, error }; }
  componentDidCatch(error: Error, errorInfo: ErrorInfo) { console.error('Application error:', error, errorInfo); }
  handleReload = () => window.location.reload();
  render() {
    if (this.state.hasError) {
      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Box sx={{ maxWidth: 400, textAlign: 'center', backgroundColor: 'background.paper', borderRadius: 3, p: 4, boxShadow: 3 }}>
              <Box sx={{ width: 64, height: 64, borderRadius: '16px', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <AlertTriangle size={32} color="#ef4444" />
              </Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>Something went wrong</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>The application encountered an unexpected error. Please try reloading the page.</Typography>
              <Button variant="contained" startIcon={<RefreshCw size={18} />} onClick={this.handleReload}>Reload Page</Button>
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <Box sx={{ mt: 3, textAlign: 'left' }}>
                  <Typography variant="caption" color="error" component="pre" sx={{ overflow: 'auto', maxHeight: 200, backgroundColor: '#fef2f2', p: 1.5, borderRadius: 1.5, fontSize: '0.7rem' }}>
                    {this.state.error.toString()}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </ThemeProvider>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [currentSection, setCurrentSection] = useState<SectionValue>('today');
  const [logTab, setLogTab] = useState<LogSubTab>('walk');
  const [bodyTab, setBodyTab] = useState<BodySubTab>('measurements');
  const [healthTab, setHealthTab] = useState<HealthSubTab>('vitals');
  const [insightsTab, setInsightsTab] = useState<InsightsSubTab>('progress');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const {
    database,
    loading,
    error,
    refreshDatabase,
    addWeight,
    addMeal,
    addExercise,
    addSupplement,
    addBodyMeasurement,
    addPhoto,
    updateGoals,
    updatePreferences,
    updateWorkoutTemplates,
    deleteWeight,
    deleteMeal,
    deleteExercise,
    deleteSupplement,
    deleteBodyMeasurement,
    deletePhoto,
    addBloodPressure,
    deleteBloodPressure,
    addLabResult,
    deleteLabResult,
    addWalk,
    deleteWalk,
    addHealthRiskSnapshot,
    deleteHealthRiskSnapshot,
    addFoodLog,
  } = useOptimizedData();

  useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated()) {
        try {
          const response = await fetch('/api/health');
          if (response.ok) setAuthenticated(true);
          else { clearAuth(); setAuthenticated(false); }
        } catch {
          clearAuth();
          setAuthenticated(false);
        }
      } else {
        setAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  const handleLogin = () => setAuthenticated(true);
  const handleLogout = useCallback(() => { clearAuth(); setAuthenticated(false); }, []);
  const handleSectionChange = useCallback((section: SectionValue) => setCurrentSection(section), []);

  const navigateToTab = useCallback((section: SectionValue, subTab?: string) => {
    setCurrentSection(section);
    if (subTab) {
      if (section === 'log') setLogTab(subTab as LogSubTab);
      if (section === 'body') setBodyTab(subTab as BodySubTab);
      if (section === 'health') setHealthTab(subTab as HealthSubTab);
      if (section === 'insights') setInsightsTab(subTab as InsightsSubTab);
    }
  }, []);

  const quickLogWalk = useCallback(async () => {
    const walk: WalkEntry = {
      id: `walk-${Date.now()}`,
      date: format(new Date(), 'yyyy-MM-dd'),
      durationMin: 30,
      withPartner: true,
    };
    await addWalk(walk);
  }, [addWalk]);

  if (!authenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login onLogin={handleLogin} />
      </ThemeProvider>
    );
  }

  const showGym = database?.preferences?.showGym ?? false;

  const logSubTabs = [
    { value: 'walk', label: 'Walk', icon: <Footprints size={16} /> },
    { value: 'weight', label: 'Weight', icon: <Scale size={16} /> },
    { value: 'bp', label: 'BP', icon: <Heart size={16} /> },
    { value: 'meal', label: 'Meal', icon: <Utensils size={16} /> },
    { value: 'supplement', label: 'Supp.', icon: <Pill size={16} /> },
    ...(showGym ? [{ value: 'gym', label: 'Gym', icon: <Dumbbell size={16} /> }] : []),
  ];

  const bodySubTabs = [
    { value: 'measurements', label: 'Measurements', icon: <Ruler size={16} /> },
    { value: 'photos', label: 'Photos', icon: <Camera size={16} /> },
  ];

  const healthSubTabs = [
    { value: 'vitals', label: 'Vitals (BP)', icon: <Heart size={16} /> },
    { value: 'labs', label: 'Labs', icon: <Beaker size={16} /> },
    { value: 'risks', label: 'Risk scores', icon: <Activity size={16} /> },
    { value: 'history', label: 'History', icon: <FileText size={16} /> },
  ];

  const insightsSubTabs = [
    { value: 'progress', label: 'Progress', icon: <TrendingUp size={16} /> },
    { value: 'performance', label: 'Performance', icon: <BarChart3 size={16} /> },
    { value: 'statistics', label: 'Statistics', icon: <PieChart size={16} /> },
  ];

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ py: 2 }}>
          <SkeletonLoader variant="statCards" />
          <Box sx={{ mt: 3 }}>
            <SkeletonLoader variant="chart" />
          </Box>
        </Box>
      );
    }

    if (error) {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="50vh" gap={2} textAlign="center" px={2}>
          <Box sx={{ width: 56, height: 56, borderRadius: '14px', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AlertTriangle size={28} color="#ef4444" />
          </Box>
          <Typography variant="h6" color="error.main" fontWeight={600}>Something went wrong</Typography>
          <Typography variant="body2" color="text.secondary" maxWidth="400px">{error}</Typography>
        </Box>
      );
    }

    switch (currentSection) {
      case 'today':
        return <Today database={database} onNavigate={navigateToTab} onQuickLogWalk={quickLogWalk} />;
      case 'log':
        return (
          <>
            <SubTabNav tabs={logSubTabs} value={logTab} onChange={(v) => setLogTab(v as LogSubTab)} />
            {logTab === 'walk' && <WalkLogger database={database} onAddWalk={addWalk} onDeleteWalk={deleteWalk} />}
            {logTab === 'weight' && <WeightTracker database={database} onAddWeight={addWeight} onUpdateGoals={updateGoals} onDeleteWeight={deleteWeight} />}
            {logTab === 'bp' && <BloodPressureTracker database={database} onAddBloodPressure={addBloodPressure} onDeleteBloodPressure={deleteBloodPressure} />}
            {logTab === 'meal' && (
              <Box>
                <DailyFoodLog database={database} onAddFoodLog={addFoodLog} />
                <MealPlanner database={database} onAddMeal={addMeal} onDeleteMeal={deleteMeal} />
              </Box>
            )}
            {logTab === 'supplement' && <SupplementTracker database={database} onAddSupplement={addSupplement} onDeleteSupplement={deleteSupplement} onUpdatePreferences={updatePreferences} />}
            {logTab === 'gym' && showGym && <ExerciseTracker database={database} onAddExercise={addExercise} onDeleteExercise={deleteExercise} updateWorkoutTemplates={updateWorkoutTemplates} />}
          </>
        );
      case 'body':
        return (
          <>
            <SubTabNav tabs={bodySubTabs} value={bodyTab} onChange={(v) => setBodyTab(v as BodySubTab)} />
            {bodyTab === 'measurements' && <BodyMeasurementTracker database={database} onAddBodyMeasurement={addBodyMeasurement} onDeleteBodyMeasurement={deleteBodyMeasurement} />}
            {bodyTab === 'photos' && <PhotoProgressTracker database={database} onAddPhoto={addPhoto} onDeletePhoto={deletePhoto} />}
          </>
        );
      case 'health':
        return (
          <>
            <SubTabNav tabs={healthSubTabs} value={healthTab} onChange={(v) => setHealthTab(v as HealthSubTab)} />
            {healthTab === 'vitals' && <BloodPressureTracker database={database} onAddBloodPressure={addBloodPressure} onDeleteBloodPressure={deleteBloodPressure} />}
            {healthTab === 'labs' && <LabResults database={database} onAddLabResult={addLabResult} onDeleteLabResult={deleteLabResult} />}
            {healthTab === 'risks' && <RiskScores database={database} onAddHealthRiskSnapshot={addHealthRiskSnapshot} onDeleteHealthRiskSnapshot={deleteHealthRiskSnapshot} />}
            {healthTab === 'history' && <MedicalHistory database={database} onUpdatePreferences={updatePreferences} />}
          </>
        );
      case 'insights':
        return (
          <>
            <SubTabNav tabs={insightsSubTabs} value={insightsTab} onChange={(v) => setInsightsTab(v as InsightsSubTab)} />
            {insightsTab === 'progress' && <ProgressTracker database={database} />}
            {insightsTab === 'performance' && <PerformanceDashboard database={database} />}
            {insightsTab === 'statistics' && <Statistics database={database} />}
          </>
        );
      case 'settings':
        return <SettingsComponent database={database} onUpdatePreferences={updatePreferences} onRefreshDatabase={refreshDatabase} onLogout={handleLogout} />;
      default:
        return <Today database={database} onNavigate={navigateToTab} onQuickLogWalk={quickLogWalk} />;
    }
  };

  const sectionTabs: { value: SectionValue; label: string; icon: React.ReactElement }[] = [
    { value: 'today', label: 'Today', icon: <Sun size={20} /> },
    { value: 'log', label: 'Log', icon: <PlusCircle size={20} /> },
    { value: 'body', label: 'Body', icon: <User size={20} /> },
    { value: 'health', label: 'Health', icon: <Stethoscope size={20} /> },
    { value: 'insights', label: 'Insights', icon: <TrendingUp size={20} /> },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column' }}>
          <AppBar position="sticky" elevation={0}>
            <Toolbar>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
                <Stethoscope size={24} />
                <Typography variant="h6" component="div" sx={{ fontWeight: 700, letterSpacing: '-0.5px', color: '#ffffff' }}>Stride</Typography>
              </Box>

              {!isMobile && (
                <Tabs
                  value={sectionTabs.some(t => t.value === currentSection) ? currentSection : 'today'}
                  onChange={(_, v) => handleSectionChange(v)}
                  textColor="inherit"
                  sx={{
                    borderBottom: 'none',
                    '& .MuiTabs-indicator': { backgroundColor: '#ffffff' },
                    '& .MuiTab-root': {
                      color: 'rgba(255,255,255,0.7)',
                      '&.Mui-selected': { color: '#ffffff' },
                    },
                  }}
                >
                  {sectionTabs.map((tab) => (
                    <Tab key={tab.value} value={tab.value} label={tab.label} icon={tab.icon} iconPosition="start" sx={{ minHeight: 64 }} />
                  ))}
                </Tabs>
              )}

              <IconButton color="inherit" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ ml: 1 }}>
                <SettingsIcon size={22} />
              </IconButton>
              <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                <MenuItem onClick={() => { setMenuAnchor(null); setCurrentSection('settings'); }}>Settings</MenuItem>
                <MenuItem onClick={() => { setMenuAnchor(null); handleLogout(); }}>Log out</MenuItem>
              </Menu>
            </Toolbar>
          </AppBar>

          <Container
            maxWidth="xl"
            sx={{
              py: { xs: 2, sm: 2.5, md: 3 },
              px: { xs: 1.5, sm: 2, md: 3 },
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              pb: { xs: 10, md: 3 },
            }}
          >
            <Fade in key={currentSection} timeout={200}>
              <Box sx={{ flex: 1 }}>
                {renderContent()}
              </Box>
            </Fade>
          </Container>

          {isMobile && (
            <BottomNav currentSection={currentSection} onSectionChange={handleSectionChange} />
          )}
        </Box>
      </ToastProvider>
    </ThemeProvider>
  );
}

function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;
